import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  NotificationType,
  showNotification,
  NotificationOptions,
} from 'store/actions/notificationActions';

const generateNotificationKey = () => {
  return `${Math.random().toString(36).substring(2, 15)}${Math.random()
    .toString(36)
    .substring(2, 15)}`;
};

interface NotificationCacheEntry {
  content: any;
  type: NotificationType;
  timestamp: number;
}

const CACHE_DURATION = 1000; // 1 second in milliseconds

const useCustomNotify = () => {
  const dispatch = useDispatch();
  const notificationCache = useRef<NotificationCacheEntry[]>([]);

  const cleanupOldEntries = useCallback(() => {
    const now = Date.now();
    notificationCache.current = notificationCache.current.filter(
      (entry) => now - entry.timestamp < CACHE_DURATION
    );
  }, []);

  const generateCacheKey = useCallback((content: any, type: NotificationType) => {
    // Convert content to string for comparison
    const contentStr = typeof content === 'string' ? content : JSON.stringify(content);
    return `${type}:${contentStr}`;
  }, []);

  const isDuplicate = useCallback(
    (content: any, type: NotificationType) => {
      const now = Date.now();
      const cacheKey = generateCacheKey(content, type);

      return notificationCache.current.some((entry) => {
        const entryCacheKey = generateCacheKey(entry.content, entry.type);
        return entryCacheKey === cacheKey && now - entry.timestamp < CACHE_DURATION;
      });
    },
    [generateCacheKey]
  );

  const addToCache = useCallback((content: any, type: NotificationType) => {
    const now = Date.now();
    notificationCache.current.push({
      content,
      type,
      timestamp: now,
    });
  }, []);

  return useCallback(
    (content: any, type: NotificationType = 'info', notificationOptions?: NotificationOptions) => {
      // Clean up old entries first
      cleanupOldEntries();

      // Check if this notification is a duplicate
      if (isDuplicate(content, type)) {
        return; // Skip displaying duplicate notification
      }

      // Add to cache and display notification
      addToCache(content, type);
      dispatch(showNotification(content, type, generateNotificationKey(), notificationOptions));
    },
    [dispatch, cleanupOldEntries, isDuplicate, addToCache]
  );
};

export default useCustomNotify;
